import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { MarketplacePayment } from '../../core/data-models/marketplace-payment.model';
import { MarketplacePaymentService } from '../../core/data-services/marketplace-payment.service';
import { MarketplacePaymentRevision } from '../../core/data-models/marketplace-payment-revision.model';
import { PaymentRevisionType } from '../../core/data-models/payment-revision-type.model';
import { ConfirmationModalWithReasonComponent } from '../../shared/components/confirmation-modal-with-reason.component';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import { ToastService } from '../../core/services/toast.service';
import { isRecord } from '../../core/utils/is-record';
import { formatPaymentAcres, formatPaymentDateTime, formatPaymentMoney, shortPaymentId } from '../../shared/utils/payment-format.utils';
import { AdjustModalComponent, AdjustModalResult } from './adjust-modal.component';

/**
 * Payment-detail page — the workhorse review surface.
 *
 * Spec: /agent-os/specs/2026-06-10-deferred-capture-admin-approval/spec.md (§ 5.3.1)
 *
 * Sections (top to bottom):
 *   1. Header card — current state, identities, amounts.
 *   2. Revision timeline — every change event chronologically.
 *   3. Action footer — context-aware buttons based on status:
 *        - pending_admin_approval / failed: Approve / Adjust / Decline
 *        - payout_pending: Release payout early
 *        - all states: Back to queue, View on Stripe (deep link)
 *
 * Confirmation flow uses the shared modals:
 *   - ConfirmationModal (Approve — no reason required)
 *   - ConfirmationModalWithReason (Decline / Early Release — reason mandatory)
 *   - AdjustModal (custom — fee preview + reason)
 */
@Component({
  selector: 'app-payment-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ConfirmationModalComponent,
    ConfirmationModalWithReasonComponent,
    AdjustModalComponent,
  ],
  templateUrl: './payment-detail.component.html',
})
export class PaymentDetailComponent implements OnInit {
  paymentId = '';
  payment: MarketplacePayment | null = null;
  revisions: MarketplacePaymentRevision[] = [];
  loading = true;
  loadError: string | null = null;

  // Modal flags
  showApprove = false;
  showDecline = false;
  showAdjust = false;
  showEarlyRelease = false;
  processing = false;

  constructor(
    private route: ActivatedRoute,
    private service: MarketplacePaymentService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.paymentId = this.route.snapshot.paramMap.get('id') || '';
    this.reload();
  }

  reload(): void {
    this.loading = true;
    this.loadError = null;
    forkJoin({
      payment: this.service.getPayment(this.paymentId),
      revisions: this.service.getRevisions(this.paymentId),
    }).subscribe({
      next: ({ payment, revisions }) => {
        this.payment = payment;
        this.revisions = revisions;
        this.loadError = payment ? null : 'Payment not found.';
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = ApiError.fromHttpError(err).message;
      },
    });
  }

  // ─── Computed view helpers ───────────────────────────────────────

  get canApprove(): boolean {
    if (!this.payment) return false;
    return this.payment.status === 'pending_admin_approval' || this.payment.status === 'failed';
  }

  get canEarlyRelease(): boolean {
    if (!this.payment) return false;
    return this.payment.status === 'payout_pending';
  }

  /**
   * True when no Stripe capture has fired on this row yet. Covers
   * `agreed_pending` (deferred-capture, pre-actuals), `pending_admin_approval`
   * (post-actuals waiting on admin), AND `disputed` filed BEFORE
   * admin approval. Drives the header relabeling ("Estimated total"
   * instead of "Total charge", etc.) — without it the page reads as
   * if the farmer has already been charged when in fact nothing has
   * moved at Stripe.
   *
   * Signal: `capturedAt == null`. The payment row's `billable_acres`
   * + `price_per_acre` + `total_charge_amount` snapshot the estimate
   * locked at bid acceptance and stay at those values until either
   * the farmer accepts actuals (then payment row gets the new acres)
   * OR the admin adjusts. In dispute or pre-actuals states, the
   * latest actuals (if any) live in the revision history — see
   * `latestReportedActuals` below.
   */
  get isPreCapture(): boolean {
    return !!this.payment && this.payment.capturedAt == null;
  }

  /**
   * The most-recent `actuals_reported` revision (the pilot's report
   * of what they actually flew), or null if the pilot hasn't reported
   * yet. When present AND different from the payment row's current
   * `billableAcres`, the header surfaces a callout: "Pilot reported
   * X.XX ac — $Y more than the estimate."
   *
   * Returns the FULL revision row so the header can render the
   * recomputed total at the new acres without re-doing the fee math
   * in the template. (Hub-side `actuals_reported` writer recomputes
   * the snapshot via `recalculateFeesFromAcres` so all financial
   * fields land populated — see [bid-service.ts] commit 2026-06-11
   * for the regression that motivated this.)
   */
  get latestReportedActuals(): MarketplacePaymentRevision | null {
    // Revisions are returned chronologically by `getRevisions` — walk
    // from the end so the LATEST report wins (pilot can in theory
    // re-report if the admin sends them back, though no UI exposes
    // that today).
    for (let i = this.revisions.length - 1; i >= 0; i--) {
      if (this.revisions[i].revisionType === 'actuals_reported') {
        return this.revisions[i];
      }
    }
    return null;
  }

  /**
   * True when there's a reported actuals revision AND the acres
   * differ from the payment row's current `billableAcres` by more
   * than the rounding-noise tolerance. Used to gate the "Pilot
   * reported …" callout — there's no point surfacing it when the
   * actuals exactly match the estimate.
   */
  get hasActualsDivergence(): boolean {
    const reported = this.latestReportedActuals;
    if (!reported?.billableAcres) return false;
    if (!this.payment?.billableAcres) return false;
    return Math.abs(reported.billableAcres - this.payment.billableAcres) > 0.005;
  }

  statusBadgeClass(s: string | null | undefined): string {
    if (s === 'pending_admin_approval') return 'badge bg-warning text-dark';
    if (s === 'agreed_pending') return 'badge bg-info text-dark';
    if (s === 'failed') return 'badge bg-danger';
    if (s === 'captured' || s === 'payout_pending') return 'badge bg-primary';
    if (s === 'transferred') return 'badge bg-success';
    if (s === 'refunded' || s === 'partially_refunded') return 'badge bg-info text-dark';
    if (s === 'disputed') return 'badge bg-warning text-dark';
    if (s === 'cancelled') return 'badge bg-secondary';
    return 'badge bg-secondary';
  }

  statusLabel(s: string | null | undefined): string {
    if (!s) return '—';
    const m: Record<string, string> = {
      pending_admin_approval: 'Pending approval',
      agreed_pending: 'Awaiting actuals',
      pending: 'Pending capture',
      captured: 'Captured',
      payout_pending: 'Payout pending',
      transferred: 'Paid out',
      disputed: 'Disputed',
      refunded: 'Refunded',
      partially_refunded: 'Partially refunded',
      failed: 'Failed',
      cancelled: 'Cancelled',
    };
    return m[s] || s;
  }

  revisionLabel(t: PaymentRevisionType | null | undefined): string {
    if (!t) return '—';
    const m: Record<PaymentRevisionType, string> = {
      estimate_locked: 'Estimate locked',
      actuals_reported: 'Pilot reported actuals',
      farmer_accepted_actuals: 'Farmer accepted actuals',
      admin_adjusted: 'Admin adjusted',
      admin_approved_capture: 'Admin approved + captured',
      admin_declined: 'Admin declined',
      admin_early_release: 'Admin released payout early',
      dispute_resolution_adjustment: 'Dispute resolved',
      overage_captured: 'Overage captured (legacy)',
    };
    return m[t] || t;
  }

  revisionToneClass(t: PaymentRevisionType | null | undefined): string {
    if (t === 'admin_approved_capture') return 'success';
    if (t === 'admin_declined' || t === 'admin_early_release') return 'warning';
    if (t === 'dispute_resolution_adjustment') return 'danger';
    if (t === 'estimate_locked' || t === 'actuals_reported' || t === 'farmer_accepted_actuals') return '';
    return 'muted';
  }

  shortId(id: string | null | undefined): string {
    return shortPaymentId(id);
  }

  formatMoney(v: number | null | undefined): string {
    return formatPaymentMoney(v);
  }

  formatAcres(v: number | null | undefined): string {
    return formatPaymentAcres(v);
  }

  formatDateTime(value: string | Date | null | undefined): string {
    return formatPaymentDateTime(value);
  }

  formatActor(actorType: string | null | undefined, profileId: string | null | undefined): string {
    const base: Record<string, string> = {
      farmer: 'Farmer',
      pilot: 'Pilot',
      admin: 'Admin',
      system: 'ADN System',
      system_auto_approval: 'ADN System (auto)',
    };
    const label = actorType ? base[actorType] || actorType : 'Unknown';
    if (profileId) {
      const short = profileId.length > 8 ? profileId.slice(0, 8) + '…' : profileId;
      return `${label} · ${short}`;
    }
    return label;
  }

  // ─── Action handlers ─────────────────────────────────────────────

  onApproveConfirmed(): void {
    if (!this.payment) return;
    this.processing = true;
    this.service.approvePayment(this.payment.id).subscribe({
      next: (outcome) => {
        this.processing = false;
        this.showApprove = false;
        if (outcome?.outcome === 'captured') {
          this.toast.success('Payment captured.');
        } else if (outcome?.outcome === 'already_captured') {
          this.toast.info('Payment was already captured.');
        } else if (outcome?.outcome === 'in_flight') {
          this.toast.info('A prior capture attempt is still processing.');
        } else {
          this.toast.info(`Outcome: ${outcome?.outcome ?? 'unknown'}`);
        }
        this.reload();
      },
      error: (err) => {
        this.processing = false;
        const apiError = ApiError.fromHttpError(err);
        if (apiError.code === 'FAILED_PERMANENT') {
          this.toast.error(`Card was declined (${declineCodeOf(apiError) ?? 'unknown reason'}).`);
        } else if (apiError.code === 'FAILED_RETRYABLE') {
          this.toast.error('Payment is temporarily unavailable — try again in a moment.');
        } else {
          this.toast.error(apiError.message);
        }
        this.showApprove = false;
      },
    });
  }

  onAdjustConfirmed(result: AdjustModalResult): void {
    if (!this.payment) return;
    this.processing = true;
    this.service
      .adjustPayment(this.payment.id, {
        billableAcres: result.billableAcres,
        pricePerAcre: result.pricePerAcre,
        reason: result.reason,
      })
      .subscribe({
        next: (outcome) => {
          this.processing = false;
          this.showAdjust = false;
          if (outcome?.outcome === 'adjusted') {
            this.toast.success('Adjustment applied. Row stays pending approval.');
          } else {
            this.toast.info(`Outcome: ${outcome?.outcome ?? 'unknown'}`);
          }
          this.reload();
        },
        error: (err) => {
          this.processing = false;
          this.toast.error(ApiError.fromHttpError(err).message);
          this.showAdjust = false;
        },
      });
  }

  onDeclineConfirmed(reason: string): void {
    if (!this.payment) return;
    this.processing = true;
    this.service.declinePayment(this.payment.id, { reason }).subscribe({
      next: () => {
        this.processing = false;
        this.showDecline = false;
        this.toast.success('Payment declined.');
        this.reload();
      },
      error: (err) => {
        this.processing = false;
        this.toast.error(ApiError.fromHttpError(err).message);
        this.showDecline = false;
      },
    });
  }

  onEarlyReleaseConfirmed(reason: string): void {
    if (!this.payment) return;
    this.processing = true;
    this.service.releasePayoutEarly(this.payment.id, reason).subscribe({
      next: (outcome) => {
        this.processing = false;
        this.showEarlyRelease = false;
        if (outcome?.outcome === 'transferred') {
          this.toast.success('Payout transferred to pilot.');
        } else if (outcome?.outcome === 'already_transferred') {
          this.toast.info('Payout was already transferred.');
        } else {
          this.toast.info(`Outcome: ${outcome?.outcome ?? 'unknown'}`);
        }
        this.reload();
      },
      error: (err) => {
        this.processing = false;
        this.toast.error(ApiError.fromHttpError(err).message);
        this.showEarlyRelease = false;
      },
    });
  }
}

/** The card decline code a `FAILED_PERMANENT` capture carries in `detail`, if any. */
function declineCodeOf(error: ApiError): string | undefined {
  const code = isRecord(error.detail) ? error.detail['declineCode'] : undefined;
  return typeof code === 'string' ? code : undefined;
}
